/**
 * Dutch translations for Supabase Auth error codes.
 *
 * @see https://supabase.com/docs/guides/auth/debugging/error-codes
 */
const authErrorMessages: Record<string, string> = {
  anonymous_provider_disabled: 'Anoniem inloggen is uitgeschakeld.',
  bad_json: 'Het verzoek bevat ongeldige gegevens.',
  bad_jwt: 'Je sessie is ongeldig. Log opnieuw in.',
  captcha_failed: 'CAPTCHA-verificatie is mislukt. Probeer het opnieuw.',
  conflict: 'Er is een conflict opgetreden. Probeer het later opnieuw.',
  email_address_invalid: 'Het e-mailadres is ongeldig.',
  email_address_not_authorized: 'Dit e-mailadres is niet toegestaan.',
  email_exists: 'Dit e-mailadres is al in gebruik.',
  email_not_confirmed: 'Je e-mailadres is nog niet bevestigd.',
  email_provider_disabled: 'Inloggen via e-mail is uitgeschakeld.',
  flow_state_expired: 'De sessie is verlopen. Probeer opnieuw in te loggen.',
  flow_state_not_found: 'De sessie is niet meer beschikbaar. Probeer opnieuw in te loggen.',
  identity_already_exists: 'Dit account is al gekoppeld.',
  identity_not_found: 'Het account is niet gevonden.',
  invalid_credentials: 'Ongeldige inloggegevens.',
  invite_not_found: 'De uitnodiging is verlopen of niet gevonden.',
  otp_disabled: 'Inloggen met een eenmalige code is uitgeschakeld.',
  otp_expired: 'De verificatiecode is verlopen. Vraag een nieuwe code aan.',
  over_email_send_rate_limit: 'Er zijn te veel e-mails verstuurd. Probeer het later opnieuw.',
  over_request_rate_limit: 'Te veel verzoeken. Probeer het over een paar minuten opnieuw.',
  over_sms_send_rate_limit: 'Er zijn te veel sms-berichten verstuurd. Probeer het later opnieuw.',
  phone_exists: 'Dit telefoonnummer is al in gebruik.',
  phone_not_confirmed: 'Je telefoonnummer is nog niet bevestigd.',
  phone_provider_disabled: 'Inloggen via telefoonnummer is uitgeschakeld.',
  provider_disabled: 'Deze inlogmethode is uitgeschakeld.',
  reauthentication_needed: 'Je moet opnieuw inloggen om door te gaan.',
  reauthentication_not_valid: 'De verificatiecode is onjuist. Probeer het opnieuw.',
  refresh_token_already_used: 'Je sessie is verlopen. Log opnieuw in.',
  session_expired: 'Je sessie is verlopen. Log opnieuw in.',
  session_not_found: 'Je sessie is niet gevonden. Log opnieuw in.',
  signup_disabled: 'Registratie is momenteel uitgeschakeld.',
  same_password: 'Je nieuwe wachtwoord moet anders zijn dan je huidige wachtwoord.',
  user_already_exists: 'Er bestaat al een account met deze gegevens.',
  user_banned: 'Dit account is geblokkeerd.',
  user_not_found: 'Gebruiker niet gevonden.',
  validation_failed: 'De ingevoerde gegevens zijn ongeldig.',
  weak_password: 'Het wachtwoord is te zwak. Kies een sterker wachtwoord.',
  unexpected_failure: 'Er is een onverwachte fout opgetreden. Probeer het later opnieuw.',
  request_timeout: 'Het verzoek duurde te lang. Probeer het opnieuw.',
};

const DEFAULT_ERROR_MESSAGE = 'Er is een fout opgetreden. Probeer het later opnieuw.';

/**
 * Translate a Supabase Auth error to a Dutch user-facing message.
 *
 * Checks the `code` property on the error object (present on `AuthApiError`)
 * and returns the corresponding Dutch message. Falls back to a generic message.
 */
export function getAuthErrorMessage(error: unknown, fallback?: string): string {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = (error as { code: string }).code;
    if (code in authErrorMessages) {
      return authErrorMessages[code];
    }
  }

  if (error instanceof Error) {
    return fallback ?? error.message;
  }

  return fallback ?? DEFAULT_ERROR_MESSAGE;
}
